import * as React from 'react';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import FormEditingHandler from 'Containers/FormEditingHandler';
import MotorForm from './Form';
import HineMMHistory from './HistoryRowData/HINEMM';
import RULMMMHistory from './HistoryRowData/RULM';
import CHOPINTENDHistory from './HistoryRowData/CHOPINTEND';
import HFMSEHistory from './HistoryRowData/HFMSE';
import MWTHistory from './HistoryRowData/6MWT';
import NSAAHistory from './HistoryRowData/NSAA';
import MFMHistory from './HistoryRowData/MFM';
import PlatformConditional from 'Components/PlatformConditional';
import { filterByDocumentType } from 'Utility/documentHandling';
import type { TMotorDocumentType, TRelevantDocumentType } from '../utils';

const MotorHistory = ({
  documents,
  startEdit,
  view,
  diagnoses,
}: {
  documents: Array<IDocumentWithTypeV2<TMotorDocumentType>>;
  startEdit: StartEdit;
  view?: IView;
  diagnoses: Array<TDiagnosis | null | undefined>;
}): React.JSX.Element => {
  const HineMMDocs = filterByDocumentType(documents, 'hine_mm');
  const RULMDocs = filterByDocumentType(documents, 'rulm');
  const CHOPINTENDDocs = filterByDocumentType(documents, 'chop_intend');
  const HFMSEDocs = filterByDocumentType(documents, 'hfms_e');
  const MWTDocs = filterByDocumentType(documents, 'mwt');
  const NSAADocs = filterByDocumentType(documents, 'nsaa');
  const MFMDocs = filterByDocumentType(documents, 'mfm');
  return (
    <React.Fragment>
      <PlatformConditional platform="sma">
        <React.Fragment>
          <HineMMHistory documents={HineMMDocs} startEdit={startEdit} view={view} />
          <RULMMMHistory documents={RULMDocs} startEdit={startEdit} view={view} />
          <CHOPINTENDHistory documents={CHOPINTENDDocs} startEdit={startEdit} view={view} />
          <HFMSEHistory documents={HFMSEDocs} startEdit={startEdit} view={view} />
          <MFMHistory documents={MFMDocs} startEdit={startEdit} view={view} />
          {diagnoses.includes('G12.1') ? <MWTHistory documents={MWTDocs} startEdit={startEdit} view={view} /> : null}
        </React.Fragment>
      </PlatformConditional>
      <PlatformConditional platform="dmd">
        <React.Fragment>
          <MWTHistory documents={MWTDocs} startEdit={startEdit} view={view} />
          <MFMHistory documents={MFMDocs} startEdit={startEdit} view={view} />
          <div style={{ marginTop: '5rem' }}>
            <NSAAHistory documents={NSAADocs} startEdit={startEdit} view={view} />
          </div>
        </React.Fragment>
      </PlatformConditional>
    </React.Fragment>
  );
};

const Motor = ({ documents }: IOwnProps): React.JSX.Element => {
  const motorDocuments = documents.filter(d => d._type !== 'diagnosis');
  const diagnoses: (TDiagnosis | null | undefined)[] = documents
    .filter(d => d._type === 'diagnosis')
    .map(d => d.diagnosis);

  return (
    <FormEditingHandler name="motor" documents={documents}>
      {(editing, startEdit, formData, view): React.JSX.Element => {
        const currentDocument = motorDocuments.find(d => d._id === (editing || view?.viewing));
        return (
          <DocumentWrapper>
            <DocumentHeader
              name={'motor'}
              headerId={
                {
                  hine_mm: 'motor.hineMM',
                  rulm: 'motor.rulm',
                  chop_intend: 'motor.chopINTEND',
                  hfms_e: 'motor.hfmsE',
                  mwt: 'motor.MWT',
                  nsaa: 'motor.nsaa',
                  mfm: 'motor.mfm',
                  '': 'motor.title',
                }[currentDocument?._type ?? ''] || 'motor.title'
              }
              editing={editing}
              editButtons={<div />}
            />
            {currentDocument ? (
              <MotorForm formData={formData} document={currentDocument} editing={editing} viewing={!!view?.viewing} />
            ) : null}
            {!editing && !view?.viewing ? (
              <MotorHistory {...{ documents: motorDocuments, startEdit, view, diagnoses }} />
            ) : undefined}
          </DocumentWrapper>
        );
      }}
    </FormEditingHandler>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
}

export default Motor;
